import { useEffect, useRef } from 'react';
import { loadGoogleMaps, type GoogleMap } from '@entities/location';

export interface InteractiveMapProps {
  /** The point the map is centered on (the fixed pin overlay sits at the panel's center). */
  readonly center: { lat: number; long: number };
  readonly zoom: number;
  /** Fired once the map settles after a USER gesture (pan/zoom) — never for a programmatic
   *  re-center, so picking a suggestion can't bounce back as a fresh "the user moved" event. */
  readonly onIdle: (center: { lat: number; long: number }, zoom: number) => void;
  /** The Maps JS API is unavailable (no key, blocked script, API not enabled) — the caller falls
   *  back to the static map. Called at most once. */
  readonly onUnavailable: () => void;
  readonly className?: string;
  readonly ariaLabel: string;
}

/** Below this the two centers are the same point for our purposes (~1 cm) — avoids a sync loop
 *  from the float noise `getCenter()` returns after a pan. */
const SAME_POINT_EPSILON = 1e-7;

/**
 * A real Google slippy map: tiles stream in continuously while panning, so there is no lag and no
 * blank edge — the behaviour the Static Maps image it replaces cannot provide (one finite PNG,
 * refetched per drag). Rendered only when `loadGoogleMaps()` succeeds; otherwise the caller keeps
 * the static map, so the picker never regresses.
 */
export function InteractiveMap({
  center,
  zoom,
  onIdle,
  onUnavailable,
  className,
  ariaLabel,
}: InteractiveMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<GoogleMap | null>(null);
  // While true, the next `idle` came from OUR setCenter/setZoom, not the user — swallow it.
  const programmaticRef = useRef(false);
  // Latest props for the `idle` listener, which is attached once and must not capture stale
  // values. Declared FIRST so it refreshes before the effects below read it on any given commit.
  const latestRef = useRef({ center, zoom, onIdle });
  useEffect(() => {
    latestRef.current = { center, zoom, onIdle };
  });

  useEffect(() => {
    let cancelled = false;
    let listener: { remove(): void } | null = null;

    void loadGoogleMaps().then((maps) => {
      if (cancelled) return;
      const container = containerRef.current;
      if (!maps || !container) {
        onUnavailable();
        return;
      }
      const { center: c, zoom: z } = latestRef.current;
      const map = new maps.Map(container, {
        center: { lat: c.lat, lng: c.long },
        zoom: z,
        // Our own pin overlay, zoom buttons and "use current location" button sit on top, so the
        // stock controls would just duplicate them.
        disableDefaultUI: true,
        // POI pins must not swallow the drag / open info windows inside the picker.
        clickableIcons: false,
        keyboardShortcuts: false,
        // One-finger drag pans instead of demanding ctrl/two fingers — this map IS the control.
        gestureHandling: 'greedy',
      });
      mapRef.current = map;
      listener = map.addListener('idle', () => {
        if (programmaticRef.current) {
          programmaticRef.current = false;
          return;
        }
        const next = map.getCenter();
        const nextZoom = map.getZoom();
        if (!next) return;
        latestRef.current.onIdle(
          { lat: next.lat(), long: next.lng() },
          nextZoom ?? latestRef.current.zoom,
        );
      });
    });

    return () => {
      cancelled = true;
      listener?.remove();
      mapRef.current = null;
    };
    // Mount-only: the map instance outlives prop changes, which are pushed in below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Push an EXTERNAL center change (a picked suggestion, "use current location") into the map.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const current = map.getCenter();
    if (
      current &&
      Math.abs(current.lat() - center.lat) < SAME_POINT_EPSILON &&
      Math.abs(current.lng() - center.long) < SAME_POINT_EPSILON
    ) {
      return; // already there (this is the echo of the user's own pan)
    }
    programmaticRef.current = true;
    map.setCenter({ lat: center.lat, lng: center.long });
  }, [center.lat, center.long]);

  // Same for the +/− buttons, which drive `zoom` from outside.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (map.getZoom() === zoom) return;
    programmaticRef.current = true;
    map.setZoom(zoom);
  }, [zoom]);

  return <div ref={containerRef} role="application" aria-label={ariaLabel} className={className} />;
}
